import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  Award, Trash2, CheckCircle, Filter, Plus, ChevronDown, ChevronUp, 
  Search, Download, RefreshCw, X, Eye, ExternalLink, DollarSign, 
  Users, Calendar, FileText, CheckCircle2, ShieldCheck, MapPin, 
  Building2, GraduationCap, Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const InputBlock = ({ label, type = 'text', value, onChange, placeholder, required = false }) => (
  <div>
    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
      {label} {required && <span className="text-[#8B1A2D]">*</span>}
    </label>
    <input
      type={type}
      required={required}
      className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium placeholder-zinc-600"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </div>
);

const SelectBlock = ({ label, value, onChange, options, required = false }) => (
  <div>
    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
      {label} {required && <span className="text-[#8B1A2D]">*</span>}
    </label>
    <select
      required={required}
      className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium"
      value={value}
      onChange={onChange}
    >
      {options.map((opt, i) => (
        <option key={i} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  </div>
);

const FormSectionHeader = ({ step, title, subtitle }) => (
  <div className="flex items-center gap-3 pb-2 border-b border-zinc-800/80 mb-4">
    <span className="w-6 h-6 rounded-full bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/40 text-[10px] font-black flex items-center justify-center">
      {step}
    </span>
    <div>
      <h3 className="text-xs font-black uppercase tracking-wider text-white">{title}</h3>
      {subtitle && <p className="text-[10px] text-zinc-500 font-medium">{subtitle}</p>}
    </div>
  </div>
);

// --- Detailed Scholarship Dossier Slide-Over ---
const ScholarshipDossierPanel = ({ scholarship, onClose }) => {
  if (!scholarship) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[600px] lg:w-[750px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex-none p-6 border-b border-zinc-800 bg-zinc-900/60">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                  Scholarship Program Dossier
                </span>
                <span className="text-xs font-mono font-bold text-zinc-400 bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">
                  {scholarship.id}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  scholarship.status === 'Active' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' :
                  scholarship.status === 'Closed' ? 'bg-red-950 text-red-400 border border-red-800' :
                  'bg-amber-950 text-amber-400 border border-amber-800'
                }`}>
                  {scholarship.status || 'Active'}
                </span>
              </div>
              <h2 className="text-2xl font-black text-white">{scholarship.name}</h2>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-zinc-400">
                <span className="flex items-center gap-1.5"><Building2 size={14} className="text-yellow-400" /> {scholarship.sponsorName || scholarship.sponsorId}</span>
                <span className="flex items-center gap-1.5"><DollarSign size={14} className="text-emerald-400" /> ${scholarship.financials?.valueUSD?.toLocaleString() || scholarship.totalPoolUSD || 0} USD</span>
                <span className="flex items-center gap-1.5"><Users size={14} className="text-amber-400" /> {scholarship.targetQuota || 25} Scholars</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Financial Breakdown */}
          <div className="p-6 bg-gradient-to-br from-yellow-950/30 via-zinc-900 to-black border border-yellow-900/40 rounded-2xl">
            <div className="text-[10px] font-black uppercase tracking-widest text-yellow-400">Financial Endowment Structure</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <span className="text-[10px] text-zinc-500 uppercase font-black">Total Grant Pool (USD)</span>
                <div className="text-lg font-mono font-black text-white mt-0.5">
                  ${(scholarship.financials?.valueUSD || scholarship.totalPoolUSD || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase font-black">Total Grant Pool (KES)</span>
                <div className="text-lg font-mono font-black text-white mt-0.5">
                  KES {(scholarship.totalPoolKES || (scholarship.financials?.valueUSD || 0) * 130).toLocaleString()}
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase font-black">Per-Scholar Cap</span>
                <div className="text-lg font-mono font-black text-emerald-400 mt-0.5">
                  ${scholarship.perStudentUSD || 1000}
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase font-black">Coverage Type</span>
                <div className="text-sm font-bold text-white mt-1">
                  {scholarship.coverageType || '100% Full Tuition'}
                </div>
              </div>
            </div>
          </div>

          {/* Academic & Demographic Criteria */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Program Category</div>
              <div className="text-sm font-bold text-white mt-1">{scholarship.category || 'Merit STEM Excellence'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Target Academic Track</div>
              <div className="text-sm font-bold text-white mt-1">{scholarship.academicTrack || 'All DIS Software Tracks'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Gender Priority</div>
              <div className="text-sm font-bold text-white mt-1">{scholarship.genderPriority || 'All Eligible Candidates'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Target Intake / Cohort</div>
              <div className="text-sm font-bold text-white mt-1">{scholarship.targetCohort || 'Cohort 2026-A'}</div>
            </div>
          </div>

          {/* Eligibility Criteria */}
          <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800 space-y-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Eligibility &amp; Selection Criteria</div>
            <p className="text-xs text-zinc-300 leading-relaxed pt-1">
              {scholarship.requirements?.eligibility || scholarship.eligibility || 'Open to high-potential candidates meeting general DIS academic standards.'}
            </p>
            {scholarship.targetCounties && (
              <div className="mt-3 pt-3 border-t border-zinc-800/80">
                <span className="text-[10px] text-zinc-500 uppercase font-black">Targeted Counties / Regions:</span>
                <p className="text-xs font-bold text-white mt-0.5">{scholarship.targetCounties}</p>
              </div>
            )}
          </div>

          {/* Timeline & Governance */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Application Deadline</div>
              <div className="text-sm font-bold text-zinc-200 mt-1">{scholarship.requirements?.deadline || scholarship.deadline || 'Rolling Basis'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Review &amp; Vetting Date</div>
              <div className="text-sm font-bold text-zinc-200 mt-1">{scholarship.reviewDate || 'Within 14 days of intake'}</div>
            </div>
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Disbursement Channel</div>
              <div className="text-sm font-bold text-emerald-400 mt-1">{scholarship.disbursementModel || 'Direct to Campus'}</div>
            </div>
          </div>

          {/* Documentation */}
          {scholarship.mouDocUrl && (
            <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Grant Agreement &amp; MoU</div>
                <div className="text-xs font-mono text-zinc-400 mt-0.5 truncate max-w-sm">{scholarship.mouDocUrl}</div>
              </div>
              <a
                href={scholarship.mouDocUrl}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5"
              >
                <ExternalLink size={13} /> View Accord
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default function ScholarshipManager({ showDemo, setShowDemo }) {
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedScholarship, setSelectedScholarship] = useState(null);

  // Form State (100% Comprehensive Schema)
  const [name, setName] = useState('');
  const [programCode, setProgramCode] = useState('');
  const [sponsorId, setSponsorId] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [category, setCategory] = useState('Merit STEM Excellence');
  const [targetCohort, setTargetCohort] = useState('Cohort 2026-A');

  const [totalPoolUSD, setTotalPoolUSD] = useState('');
  const [totalPoolKES, setTotalPoolKES] = useState('');
  const [perStudentUSD, setPerStudentUSD] = useState('');
  const [coverageType, setCoverageType] = useState('100% Full Tuition Coverage');
  const [disbursementModel, setDisbursementModel] = useState('Direct to Campus Ledger');
  const [targetQuota, setTargetQuota] = useState('25');

  const [targetCounties, setTargetCounties] = useState('');
  const [minGpaRequirement, setMinGpaRequirement] = useState('KCSE C+ or Equivalent');
  const [genderPriority, setGenderPriority] = useState('All Eligible Candidates');
  const [academicTrack, setAcademicTrack] = useState('All DIS Technical Tracks');
  const [eligibility, setEligibility] = useState('');

  const [openingDate, setOpeningDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [mouDocUrl, setMouDocUrl] = useState('');
  const [scholarshipStatus, setScholarshipStatus] = useState('Active');

  const fetchData = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'scholarships'));
      const data = [];
      snap.forEach(d => data.push({ id: d.id, ...d.data() }));
      if (showDemo) { 
        data.unshift(...getDemoDataForCollection('scholarships')); 
      }
      setScholarships(data);

      const sponsorSnap = await getDocs(collection(db, 'sponsors'));
      const sponsorData = [];
      sponsorSnap.forEach(d => sponsorData.push({ id: d.id, name: d.data().name || d.data().orgName || d.id }));
      setSponsors(sponsorData);
    } catch (e) { 
      console.error('Error fetching scholarships:', e); 
    }
    setLoading(false);
  };

  useEffect(() => { 
    fetchData(); 
  }, [showDemo]);

  const handleSponsorSelect = (selectedId) => {
    setSponsorId(selectedId);
    const found = sponsors.find(s => s.id === selectedId);
    if (found) setSponsorName(found.name);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name.trim() || !sponsorId) {
      alert('Scholarship Name and Sponsoring Organization are required.');
      return;
    }

    setStatusMessage('saving');
    const slug = (programCode.trim() || name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-'));

    try {
      await setDoc(doc(db, 'scholarships', slug), {
        name: name.trim(),
        programCode: slug.toUpperCase(),
        sponsorId,
        sponsorName: sponsorName || sponsorId,
        category,
        targetCohort,

        financials: { 
          valueUSD: parseInt(totalPoolUSD) || 0,
          valueKES: parseInt(totalPoolKES) || (parseInt(totalPoolUSD) || 0) * 130
        },
        totalPoolUSD: parseInt(totalPoolUSD) || 0,
        totalPoolKES: parseInt(totalPoolKES) || (parseInt(totalPoolUSD) || 0) * 130,
        perStudentUSD: parseInt(perStudentUSD) || 1000,
        coverageType,
        disbursementModel,
        targetQuota: parseInt(targetQuota) || 25,

        targetCounties: targetCounties.trim(),
        minGpaRequirement,
        genderPriority,
        academicTrack,
        requirements: { 
          eligibility: eligibility.trim(), 
          deadline,
          openingDate,
          reviewDate
        },
        eligibility: eligibility.trim(),
        deadline,
        reviewDate,
        mouDocUrl: mouDocUrl.trim(),

        status: scholarshipStatus,
        createdAt: new Date().toISOString(),
        addedAt: new Date().toISOString()
      }, { merge: true });

      // Reset
      setName('');
      setProgramCode('');
      setSponsorId('');
      setSponsorName('');
      setTotalPoolUSD('');
      setTotalPoolKES('');
      setPerStudentUSD('');
      setTargetCounties('');
      setEligibility('');
      setDeadline('');
      setReviewDate('');
      setMouDocUrl('');
      setScholarshipStatus('Active');

      setStatusMessage('success');
      fetchData();
      setTimeout(() => {
        setStatusMessage('');
        setIsFormOpen(false);
      }, 2500);
    } catch (e) { 
      console.error('Error adding scholarship:', e); 
      setStatusMessage('error'); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete scholarship package ${id}?`)) return;
    try { 
      await deleteDoc(doc(db, 'scholarships', id)); 
      fetchData(); 
    } catch (e) { 
      console.error(e); 
    }
  };

  // Filtering
  const filteredScholarships = useMemo(() => {
    return scholarships.filter(s => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = 
        (s.name || '').toLowerCase().includes(q) ||
        (s.id || '').toLowerCase().includes(q) ||
        (s.sponsorName || s.sponsorId || '').toLowerCase().includes(q) ||
        (s.category || '').toLowerCase().includes(q) ||
        (s.targetCounties || '').toLowerCase().includes(q);

      const matchesStatus = filterStatus === 'all' || (s.status || 'Active') === filterStatus;
      const matchesCategory = filterCategory === 'all' || s.category === filterCategory;

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [scholarships, searchQuery, filterStatus, filterCategory]);

  // Categories list
  const categories = useMemo(() => {
    const set = new Set();
    scholarships.forEach(s => { if (s.category) set.add(s.category); });
    return Array.from(set).sort();
  }, [scholarships]);

  // CSV Export
  const exportToCSV = () => {
    const headers = [
      'Scholarship ID', 'Program Name', 'Sponsor', 'Category', 'Cohort',
      'Total Pool USD', 'Total Pool KES', 'Per Student USD', 'Coverage',
      'Target Quota', 'Track', 'Deadline', 'Status'
    ];
    const rows = filteredScholarships.map(s => [
      s.id,
      `"${s.name || ''}"`,
      `"${s.sponsorName || s.sponsorId || ''}"`,
      `"${s.category || ''}"`,
      `"${s.targetCohort || ''}"`,
      s.financials?.valueUSD || s.totalPoolUSD || 0,
      s.totalPoolKES || (s.financials?.valueUSD || 0) * 130,
      s.perStudentUSD || 1000,
      `"${s.coverageType || '100%'}"`,
      s.targetQuota || 25,
      `"${s.academicTrack || 'All'}"`,
      s.requirements?.deadline || s.deadline || 'Open',
      s.status || 'Active'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `DIS_Scholarships_Ledger_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-2xl">
                <Award size={22} />
              </span>
              <span className="px-3 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 10 &bull; Institutional Endowments &amp; Bursaries
              </span>
            </div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Scholarship &amp; Grant Program Registry
            </h1>
            <p className="text-zinc-400 text-xs font-bold max-w-2xl leading-relaxed">
              Administer donor endowments, merit bursaries, and affirmative action funds. Coordinate student allocation quotas, per-scholar funding caps, and institutional disbursements in real-time.
            </p>
          </div>

          <div className="flex items-center gap-4 bg-zinc-900/90 border border-zinc-800 p-4 rounded-2xl">
            <div className="text-right">
              <div className="text-[10px] font-black text-zinc-400 uppercase tracking-wider">Active Programs</div>
              <div className="text-2xl font-black text-yellow-400">
                {scholarships.length} <span className="text-xs text-zinc-500 font-bold">Grants</span>
              </div>
            </div>
            <button
              onClick={() => setIsFormOpen(!isFormOpen)}
              className="flex items-center gap-2 px-5 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 cursor-pointer"
            >
              <Plus size={15} /> {isFormOpen ? 'Close Form' : 'Register New Grant'}
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible Full Form */}
      {isFormOpen && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-6 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
            <div>
              <h2 className="text-base font-black text-white uppercase tracking-wider flex items-center gap-2">
                <Plus size={18} className="text-[#8B1A2D]" /> Register New Scholarship Endowment Package
              </h2>
              <p className="text-xs text-zinc-500">Comprehensive donor endowment governance and candidate eligibility mapping</p>
            </div>
            <button 
              onClick={() => setIsFormOpen(false)}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl"
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleAdd} className="space-y-6">
            {/* Step 1: Program Identity */}
            <div>
              <FormSectionHeader 
                step="1" 
                title="Endowment Identity &amp; Sponsoring Institution" 
                subtitle="Program naming, docket identifier, and parent philanthropic entity"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputBlock label="Scholarship Program Name" placeholder="e.g. 2026 Women In Tech STEM Grant" value={name} onChange={e => setName(e.target.value)} required />
                <InputBlock label="Program Code / Docket Slug" placeholder="e.g. STEM-WOMEN-2026" value={programCode} onChange={e => setProgramCode(e.target.value.toUpperCase())} />
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
                    Sponsoring Entity <span className="text-[#8B1A2D]">*</span>
                  </label>
                  <select
                    required
                    value={sponsorId}
                    onChange={e => handleSponsorSelect(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] font-medium"
                  >
                    <option value="">-- Select Registered Sponsor --</option>
                    {sponsors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                    <option value="direct_endowment">Direct Foundation / Sovereign Fund</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <SelectBlock
                  label="Program Category"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  options={[
                    "Merit STEM Excellence",
                    "Need-Based Full Ride",
                    "Women in Technology Equity",
                    "County Rural Dev Grant",
                    "TVET Upskilling Bursary",
                    "Corporate Fellowship"
                  ]}
                  required
                />
                <SelectBlock
                  label="Target Academic Intake Cohort"
                  value={targetCohort}
                  onChange={e => setTargetCohort(e.target.value)}
                  options={["Cohort 2026-A", "Cohort 2026-B", "Cohort 2026-C", "Rolling Admission"]}
                  required
                />
                <SelectBlock
                  label="Initial Program Status"
                  value={scholarshipStatus}
                  onChange={e => setScholarshipStatus(e.target.value)}
                  options={["Active", "Upcoming", "Fully Allocated", "Closed"]}
                  required
                />
              </div>
            </div>

            {/* Step 2: Financials & Quotas */}
            <div>
              <FormSectionHeader 
                step="2" 
                title="Financial Endowment &amp; Award Quotas" 
                subtitle="Total fund sizing, per-scholar caps, and settlement routing"
              />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <InputBlock label="Total Grant Pool (USD)" type="number" placeholder="e.g. 50000" value={totalPoolUSD} onChange={e => setTotalPoolUSD(e.target.value)} required />
                <InputBlock label="Total Grant Pool (KES)" type="number" placeholder="e.g. 6500000" value={totalPoolKES} onChange={e => setTotalPoolKES(e.target.value)} />
                <InputBlock label="Per-Scholar Award Cap (USD)" type="number" placeholder="e.g. 1000" value={perStudentUSD} onChange={e => setPerStudentUSD(e.target.value)} required />
                <InputBlock label="Target Scholar Allocation Quota" type="number" placeholder="e.g. 50" value={targetQuota} onChange={e => setTargetQuota(e.target.value)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <SelectBlock
                  label="Tuition Coverage Model"
                  value={coverageType}
                  onChange={e => setCoverageType(e.target.value)}
                  options={[
                    "100% Full Tuition Coverage",
                    "90% Subsidized Hybrid",
                    "50% Matching Grant (Co-Pay)",
                    "100% Full Tuition + Hardware Stipend"
                  ]}
                  required
                />
                <SelectBlock
                  label="Disbursement &amp; Settlement Channel"
                  value={disbursementModel}
                  onChange={e => setDisbursementModel(e.target.value)}
                  options={[
                    "Direct to Campus Ledger",
                    "Student Digital Wallet (DIS Escrow)",
                    "Milestone Reimbursement (Post-Assessment)"
                  ]}
                  required
                />
              </div>
            </div>

            {/* Step 3: Eligibility & Targeting */}
            <div>
              <FormSectionHeader 
                step="3" 
                title="Eligibility Criteria &amp; Demographics" 
                subtitle="Geographic, gender, and academic admission criteria"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputBlock label="Target Counties / Geographic Regions" placeholder="e.g. Nakuru, Baringo, Kisumu, Turkana" value={targetCounties} onChange={e => setTargetCounties(e.target.value)} />
                <SelectBlock
                  label="Academic Entry Baseline"
                  value={minGpaRequirement}
                  onChange={e => setMinGpaRequirement(e.target.value)}
                  options={[
                    "KCSE C+ or Equivalent",
                    "KCSE C Plain or TVET Diploma",
                    "Open to All High School Leavers",
                    "Bachelor Degree Graduate"
                  ]}
                  required
                />
                <SelectBlock
                  label="Gender &amp; Equity Priority"
                  value={genderPriority}
                  onChange={e => setGenderPriority(e.target.value)}
                  options={[
                    "All Eligible Candidates",
                    "Female Candidates Only (100% Women)",
                    "Affirmative Action / Marginalized Priority"
                  ]}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <SelectBlock
                  label="Technical Track Eligibility"
                  value={academicTrack}
                  onChange={e => setAcademicTrack(e.target.value)}
                  options={[
                    "All DIS Technical Tracks",
                    "Full-Stack Web3 & Cloud Engineering",
                    "AI, Data Science & Machine Learning",
                    "Cyber Security & Infrastructure",
                    "Creative Digital Media & UI/UX"
                  ]}
                  required
                />
                <InputBlock label="MoU / Grant Agreement Document URL" placeholder="https://drive.google.com/..." value={mouDocUrl} onChange={e => setMouDocUrl(e.target.value)} />
              </div>
              <div className="mt-4">
                <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
                  Specific Eligibility Narrative &amp; Prerequisite Rules
                </label>
                <textarea
                  value={eligibility}
                  onChange={e => setEligibility(e.target.value)}
                  placeholder="Outline household income limits, aptitude test requirements, and vetting steps..."
                  rows={2}
                  className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] placeholder-zinc-600 font-medium"
                />
              </div>
            </div>

            {/* Step 4: Governance Timeline */}
            <div>
              <FormSectionHeader 
                step="4" 
                title="Governance Milestones &amp; Deadlines" 
                subtitle="Application open, closing, and board review dates"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputBlock label="Application Opening Date" type="date" value={openingDate} onChange={e => setOpeningDate(e.target.value)} />
                <InputBlock label="Submission Deadline" type="date" value={deadline} onChange={e => setDeadline(e.target.value)} required />
                <InputBlock label="Board Review &amp; Vetting Date" type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)} />
              </div>
            </div>

            {/* Submit Bar */}
            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <div>
                {statusMessage === 'success' && (
                  <span className="flex items-center gap-2 text-emerald-400 text-xs font-black uppercase tracking-wider">
                    <CheckCircle2 size={16} /> Scholarship Program Registered with Full Schema!
                  </span>
                )}
                {statusMessage === 'saving' && (
                  <span className="text-yellow-400 text-xs font-black uppercase tracking-wider flex items-center gap-2">
                    <RefreshCw size={14} className="animate-spin" /> Registering Endowment Package...
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-6 py-2.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-bold uppercase tracking-wider rounded-xl transition-colors border border-zinc-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={statusMessage === 'saving'}
                  className="flex items-center gap-2 px-8 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 cursor-pointer disabled:opacity-50"
                >
                  <Plus size={14} /> Commit Scholarship Program
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Spreadsheet Master Table Container */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-4">
        {/* Table Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-xl">
              <Award size={18} />
            </span>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-wider">
                Endowments &amp; Bursaries Master Sheet ({filteredScholarships.length} of {scholarships.length})
              </h2>
              <p className="text-[10px] text-zinc-500">Google Sheets-grade spreadsheet with freeze panes and multi-parameter filtering</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[220px]">
              <Search className="absolute left-3 top-2.5 text-zinc-500" size={14} />
              <input
                type="text"
                placeholder="Search Program, Sponsor, County..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-[#8B1A2D] placeholder-zinc-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All Categories</option>
              {categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Fully Allocated">Fully Allocated</option>
              <option value="Closed">Closed</option>
            </select>

            {/* CSV Export */}
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 rounded-xl text-xs font-bold transition-colors"
              title="Export to CSV"
            >
              <Download size={13} /> Export CSV
            </button>

            {/* Show Demo Toggle */}
            <button
              onClick={() => setShowDemo(!showDemo)}
              className={`text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-xl border transition-all ${
                showDemo ? 'border-[#8B1A2D] text-white bg-[#8B1A2D]' : 'border-zinc-800 text-zinc-400 bg-zinc-900 hover:text-white'
              }`}
            >
              {showDemo ? 'Hide Demo' : 'Show Demo'}
            </button>

            {/* Refresh */}
            <button
              onClick={fetchData}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors"
              title="Refresh Directory"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Master Freeze-Pane Table */}
        {loading ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest animate-pulse">
            Loading Scholarships Registry...
          </div>
        ) : filteredScholarships.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest">
            No scholarship programs match your query.
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-800/80 rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1600px]">
              <thead>
                <tr className="bg-zinc-900/90 border-b border-zinc-800 text-[9px] font-black uppercase tracking-widest text-zinc-400">
                  {/* Sticky 0: Index */}
                  <th className="py-3 px-3 text-center w-12 sticky left-0 z-30 bg-zinc-900 border-r border-zinc-800">
                    #
                  </th>
                  {/* Sticky 1: Program Name & Code */}
                  <th className="py-3 px-4 min-w-[240px] sticky left-12 z-30 bg-zinc-900 border-r border-zinc-800 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                    Scholarship Program
                  </th>
                  <th className="py-3 px-4">Sponsoring Institution</th>
                  <th className="py-3 px-4">Category Tier</th>
                  <th className="py-3 px-4 text-right">Total Grant Pool</th>
                  <th className="py-3 px-4 text-right">Per-Scholar Cap</th>
                  <th className="py-3 px-4">Coverage Model</th>
                  <th className="py-3 px-4 text-center">Scholar Quota</th>
                  <th className="py-3 px-4">Academic Track</th>
                  <th className="py-3 px-4">Target Counties</th>
                  <th className="py-3 px-4">Gender Policy</th>
                  <th className="py-3 px-4">Application Deadline</th>
                  <th className="py-3 px-4">Disbursement Model</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-xs font-medium">
                {filteredScholarships.map((sch, idx) => {
                  const valUSD = sch.financials?.valueUSD || sch.totalPoolUSD || 0;
                  const valKES = sch.totalPoolKES || valUSD * 130;

                  return (
                    <tr 
                      key={sch.id || idx}
                      onClick={() => setSelectedScholarship(sch)}
                      className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                    >
                      {/* Sticky 0 */}
                      <td className="py-3.5 px-3 text-center text-zinc-500 font-mono sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80">
                        {idx + 1}
                      </td>

                      {/* Sticky 1 */}
                      <td className="py-3.5 px-4 sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-bold text-white group-hover:text-yellow-400 transition-colors">
                              {sch.name}
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                              {sch.programCode || sch.id}
                            </div>
                          </div>
                          <Eye size={14} className="text-zinc-600 group-hover:text-yellow-400 transition-colors" />
                        </div>
                      </td>

                      {/* Sponsor */}
                      <td className="py-3.5 px-4 font-bold text-zinc-300">
                        {sch.sponsorName || sch.sponsorId || 'Direct Endowment'}
                      </td>

                      {/* Category */}
                      <td className="py-3.5 px-4">
                        <span className="px-2.5 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-[10px] font-black uppercase tracking-wider rounded-full">
                          {sch.category || 'Merit STEM'}
                        </span>
                      </td>

                      {/* Total Pool */}
                      <td className="py-3.5 px-4 text-right font-mono">
                        <div className="font-black text-emerald-400">${valUSD.toLocaleString()} USD</div>
                        <div className="text-[10px] text-zinc-500">KES {valKES.toLocaleString()}</div>
                      </td>

                      {/* Per-Scholar Cap */}
                      <td className="py-3.5 px-4 text-right font-mono font-bold text-zinc-200">
                        ${(sch.perStudentUSD || 1000).toLocaleString()}
                      </td>

                      {/* Coverage Model */}
                      <td className="py-3.5 px-4 text-zinc-300">
                        {sch.coverageType || '100% Full Tuition'}
                      </td>

                      {/* Target Quota */}
                      <td className="py-3.5 px-4 text-center">
                        <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 text-white group-hover:border-yellow-500/40 font-mono font-bold rounded-full transition-colors">
                          {sch.targetQuota || 25} Scholars
                        </span>
                      </td>

                      {/* Academic Track */}
                      <td className="py-3.5 px-4 text-zinc-300 truncate max-w-[150px]">
                        {sch.academicTrack || 'All Tracks'}
                      </td>

                      {/* Target Counties */}
                      <td className="py-3.5 px-4 text-zinc-400 truncate max-w-[150px]">
                        {sch.targetCounties || 'National (All Counties)'}
                      </td>

                      {/* Gender Policy */}
                      <td className="py-3.5 px-4 text-zinc-400 text-[11px]">
                        {sch.genderPriority || 'All Eligible'}
                      </td>

                      {/* Deadline */}
                      <td className="py-3.5 px-4 font-mono text-zinc-300">
                        {sch.requirements?.deadline || sch.deadline || 'Rolling'}
                      </td>

                      {/* Disbursement Model */}
                      <td className="py-3.5 px-4 text-zinc-400 text-[11px] truncate max-w-[140px]">
                        {sch.disbursementModel || 'Direct to Campus'}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          (sch.status || 'Active') === 'Active'
                            ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800'
                            : (sch.status || 'Active') === 'Closed'
                            ? 'bg-red-950/80 text-red-400 border border-red-800'
                            : 'bg-amber-950/80 text-amber-400 border border-amber-800'
                        }`}>
                          {sch.status || 'Active'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSelectedScholarship(sch); 
                            }}
                            className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                            title="Inspect Grant Dossier"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(sch.id); }}
                            className="p-1.5 text-zinc-500 hover:text-red-400 bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                            title="Delete Program"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slide-over Detailed Dossier Drawer */}
      {selectedScholarship && (
        <ScholarshipDossierPanel
          scholarship={selectedScholarship}
          onClose={() => setSelectedScholarship(null)}
        />
      )}
    </div>
  );
}
